// File: src/App.js
import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";

// Import Sidebar from your components folder
import Sidebar from "./components/Sidebar";

// Lazy Loaded Pages
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const NotFound = lazy(() => import("./pages/NotFound"));
const UserHomeScreen = lazy(() => import("./pages/UserHomeScreen"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const TeamLeaderDashboard = lazy(() => import("./pages/teamleader/TeamLeaderDashboard"));
const HRDashboard = lazy(() => import("./pages/hrdashboard/HRDashboard"));
const EmployeeDashboard = lazy(() => import("./pages/employee/EmployeeDashboard"));
const AccountsDashboard = lazy(() => import("./pages/account/AccountsDashboard"));
const FinanceDashboard = lazy(() => import("./pages/finance/FinanceDashboard"));
const ProcurementDashboard = lazy(() => import("./pages/Procurement/ProcurementDashboard"));
const AssetLog = lazy(() => import("./pages/AssetLog"));
const IndentForm = lazy(() => import("./pages/employee/IndentForm"));
const AssetRequests = lazy(() => import("./pages/Procurement/AssetRequests.js"));
const LazyAttendance = lazy(() => import("./pages/employee/Attendance.js")); // Or move/copy it if you need role-specific


// Helper to get user role from sessionStorage (ensuring lowercase for consistency)
const getUserRole = () => {
  const role = sessionStorage.getItem("userRole") || "";
  return role.toLowerCase();
};

// MainLayout with fixed Sidebar
const MainLayout = ({ handleLogout }) => {
  const sidebarWidth = 220; // Adjust as needed

  return (
    <div>
      {/* Fixed Sidebar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: sidebarWidth,
          height: "100vh",
          backgroundColor: "#f8f9fa",
          borderRight: "1px solid #ddd",
          overflowY: "auto",
          zIndex: 1000,
        }}
      >
        <Sidebar userRole={getUserRole()} handleLogout={handleLogout} />
      </div>

      {/* Main Content */}
      <div
        style={{
          marginLeft: sidebarWidth,
          padding: "20px",
          minHeight: "100vh",
        }}
      >
        <Outlet />
      </div>
    </div>
  );
};

// Role-Based Protected Route component
const ProtectedRoute = ({ element, allowedRoles }) => {
  const userRole = getUserRole();
  return userRole && allowedRoles.includes(userRole)
    ? element
    : <Navigate to="/login" replace />;
};

const App = () => {
  // Example logout function
  const handleLogout = () => {
    sessionStorage.removeItem("userRole");
    sessionStorage.removeItem("userData");
    window.location.href = "/login";
  };

  return (
    <Router>
      <Suspense
        fallback={
          <div className="d-flex justify-content-center align-items-center vh-100">
            <h4>Loading...</h4>
          </div>
        }
      >
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/homeScreen" element={<UserHomeScreen />} />

          {/* Protected Routes for individual dashboards */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute
                element={<AdminDashboard />}
                allowedRoles={["admin"]}
              />
            }
          />
          <Route
            path="/team-leader/*"
            element={
              <ProtectedRoute
                element={<TeamLeaderDashboard />}
                allowedRoles={["team_leader"]}
              />
            }
          />
          <Route
            path="/hr/*"
            element={
              <ProtectedRoute
                element={<HRDashboard />}
                allowedRoles={["hr"]}
              />
            }
          />
          <Route
            path="/employee/*"
            element={
              <ProtectedRoute
                element={<EmployeeDashboard />}
                allowedRoles={["employee"]}
              />
            }
          />
          <Route
            path="/accounts/*"
            element={
              <ProtectedRoute
                element={<AccountsDashboard />}
                allowedRoles={["accounts"]}
              />
            }
          />
          <Route
            path="/finance/*"
            element={
              <ProtectedRoute
                element={<FinanceDashboard />}
                allowedRoles={["finance"]}
              />
            }
          />
          <Route
            path="/procurement/*"
            element={
              <ProtectedRoute
                element={<ProcurementDashboard />}
                allowedRoles={["procurement"]}
              />
            }
          />

          {/* Protected Routes with fixed Sidebar */}
          <Route element={<MainLayout handleLogout={handleLogout} />}>
            {/* Asset Logs Routes for all non-employee roles */}
            <Route
              path="/admin/assetlogs"
              element={
                <ProtectedRoute
                  element={<AssetLog />}
                  allowedRoles={["admin", "team_leader", "hr", "accounts", "finance", "procurement"]}
                />
              }
            />
            <Route
              path="/team-leader/assetlogs"
              element={
                <ProtectedRoute
                  element={<AssetLog />}
                  allowedRoles={["admin", "team_leader", "hr", "accounts", "finance", "procurement"]}
                />
              }
            />
            <Route
              path="/accounts/assetlogs"
              element={
                <ProtectedRoute
                  element={<AssetLog />}
                  allowedRoles={["admin", "team_leader", "hr", "accounts", "finance", "procurement"]}
                />
              }
            />
            <Route
              path="/procurement/assetlogs"
              element={
                <ProtectedRoute
                  element={<AssetLog />}
                  allowedRoles={["admin", "team_leader", "hr", "accounts", "finance", "procurement"]}
                />
              }
            />
            {/* Asset Logs for everyone except employees */}
            <Route path="/procurement/assetlogs" element={
              <ProtectedRoute element={<AssetLog />} allowedRoles={[/*...*/]} />
            } />

            {/* Procurement → Asset Requests */}
            <Route
              path="/procurement/assetrequests"
              element={
                <ProtectedRoute
                  element={<AssetRequests />}
                  allowedRoles={["procurement"]}
                />
              }
            />
            <Route
              path="/procurement/assetrequests/:id"
              element={
                <ProtectedRoute
                  element={<IndentForm role={getUserRole()} />}
                  allowedRoles={["procurement"]}
                />
              }
            />

            {/* Indent Form for creating/editing indent requests */}
            <Route
              path="/indent-form/:id"
              element={
                <ProtectedRoute
                  element={<IndentForm role={getUserRole()} />}
                  allowedRoles={["employee", "team_leader", "admin", "hr", "accounts", "finance", "procurement"]}
                />
              }
            />
            <Route
  path="/accounts/attendance"
  element={
    <ProtectedRoute
      element={<LazyAttendance />}
      allowedRoles={["accounts"]}
    />
  }
/>
<Route
  path="/finance/attendance"
  element={
    <ProtectedRoute
      element={<LazyAttendance />}
      allowedRoles={["finance"]}
    />
  }
/>
<Route
  path="/procurement/attendance"
  element={
    <ProtectedRoute
      element={<LazyAttendance />}
      allowedRoles={["procurement"]}
    />
  }
/>

          </Route>

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <ToastContainer
          position="top-center"
          autoClose={1000}
          hideProgressBar
          closeOnClick
          theme="colored"
        />
      </Suspense>
    </Router>
  );
};

export default App;
